import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Tag, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { useLayout } from '../context/LayoutContext';
import ConfirmationModal from '../components/ConfirmationModal';
import FigmaPasteArea from '../components/FigmaPasteArea';

interface Tag {
  id: string;
  name: string;
}

interface TemplateData {
  id: string;
  name: string;
  thumbnail_url: string;
  template_url: string;
  template_type: string;
  figma_html_data?: string;
  tags: Tag[];
}

export default function TemplateEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { triggerTemplateRefresh } = useLayout();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [name, setName] = useState('');
  const [templateType, setTemplateType] = useState<'elementor' | 'figma'>('elementor');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [currentThumbnailUrl, setCurrentThumbnailUrl] = useState('');
  const [originalThumbnailUrl, setOriginalThumbnailUrl] = useState<string | null>(null);
  const [jsonContent, setJsonContent] = useState('');
  const [figmaHtmlData, setFigmaHtmlData] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    loadTemplateAndTags();
  }, [id]);

  const loadTemplateAndTags = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch template data
      const { data: templateData, error: templateError } = await supabase
        .from('templates')
        .select(`
          *,
          template_tags (
            tags (
              id,
              name
            )
          )
        `)
        .eq('id', id)
        .single();

      if (templateError) throw templateError;

      // Fetch all available tags
      const { data: tagsData, error: tagsError } = await supabase
        .from('tags')
        .select('*');

      if (tagsError) throw tagsError;

      // Set template data
      setName(templateData.name);
      setTemplateType(templateData.template_type || 'elementor');
      setCurrentThumbnailUrl(templateData.thumbnail_url);
      setOriginalThumbnailUrl(templateData.thumbnail_url);
      setJsonContent(templateData.json_content ? JSON.stringify(templateData.json_content, null, 2) : '');
      setFigmaHtmlData(templateData.figma_html_data || '');
      
      // Set tags
      setAvailableTags(tagsData || []);
      setSelectedTags(templateData.template_tags.map((tt: any) => tt.tags.id));

    } catch (err) {
      console.error('Error loading template:', err);
      setError(err instanceof Error ? err.message : 'Failed to load template');
      toast.error('Failed to load template');
    } finally {
      setIsLoading(false);
    }
  };

  const isValidUUID = (uuid: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  const handleDelete = async () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!id || !isValidUUID(id)) {
      toast.error('Invalid template ID format');
      return;
    }

    setIsDeleting(true);

    try {
      // First delete associated template_tags
      const { error: tagDeleteError } = await supabase
        .from('template_tags')
        .delete()
        .eq('template_id', id);

      if (tagDeleteError) {
        console.error('Error deleting template tags:', tagDeleteError);
        throw new Error('Failed to delete template tags');
      }

      // Then delete the template
      const { error: templateDeleteError } = await supabase
        .from('templates')
        .delete()
        .eq('id', id);

      if (templateDeleteError) {
        console.error('Error deleting template:', templateDeleteError);
        throw templateDeleteError;
      }

      toast.success('Template deleted successfully');
      navigate('/');
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'object' && error !== null && 'message' in error
          ? String(error.message)
          : 'Failed to delete template';
      
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRemoveThumbnail = () => {
    setThumbnail(null);
    setThumbnailPreview(null);
    setCurrentThumbnailUrl('');
    
    // Clear the file input
    const fileInput = document.getElementById('thumbnail') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    
    toast.success('Thumbnail removed');
  };

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  
  console.log('=== TEMPLATE EDIT SUBMIT START ===');
  console.log('Current state:', {
    thumbnail: thumbnail ? { name: thumbnail.name, size: thumbnail.size } : null,
    thumbnailPreview,
    currentThumbnailUrl,
    originalThumbnailUrl
  });

  if (selectedTags.length === 0) {
    toast.error('Select at least 1 tag');
    return;
  }

  // Validate based on template type
  if (templateType === 'elementor' && jsonContent) {
    try {
      const parsed = JSON.parse(jsonContent);
      if (!parsed.elements || !Array.isArray(parsed.elements)) {
        toast.error('JSON must contain an elements array');
        return;
      }
    } catch (error) {
      toast.error('Invalid JSON format');
      return;
    }
  } else if (templateType === 'figma' && !figmaHtmlData) {
    toast.error('Figma templates must contain HTML data');
    return;
  }

  setIsSaving(true);

  try {
    let thumbnailUrl = currentThumbnailUrl;
    let parsedJsonContent = null;

    // Parse JSON content for Elementor templates
    if (templateType === 'elementor' && jsonContent) {
      parsedJsonContent = JSON.parse(jsonContent);
    }

    // Upload new thumbnail if provided
    if (thumbnail) {
      console.log('=== UPLOADING NEW THUMBNAIL ===');
      console.log('New thumbnail file:', { name: thumbnail.name, size: thumbnail.size, type: thumbnail.type });
      
      // Delete old thumbnail from storage if it exists
      if (currentThumbnailUrl) {
        try {
          // Extract the full path from the current thumbnail URL
          // URL format: https://[project-id].supabase.co/storage/v1/object/public/thumbnails/thumbnails/[filename]
          // We need to get everything after '/public/thumbnails/'
          const publicIndex = currentThumbnailUrl.indexOf('/public/thumbnails/');
          let filePath = '';
          
          if (publicIndex !== -1) {
            // Extract the path after '/public/thumbnails/'
            filePath = currentThumbnailUrl.substring(publicIndex + '/public/thumbnails/'.length);
          } else {
            // Fallback: just get the filename
            const urlParts = currentThumbnailUrl.split('/');
            filePath = urlParts[urlParts.length - 1];
          }
          
          if (filePath) {
            console.log('Deleting old thumbnail:', filePath);
            const { error: deleteError } = await supabase.storage
              .from('thumbnails')
              .remove([filePath]);
            
            if (deleteError) {
              console.warn('Failed to delete old thumbnail:', deleteError);
              // Don't throw error here, continue with upload
            } else {
              console.log('Old thumbnail deleted successfully');
            }
          }
        } catch (deleteError) {
          console.warn('Error deleting old thumbnail:', deleteError);
          // Continue with upload even if deletion fails
        }
      }

      // Upload new thumbnail
      const thumbnailPath = `thumbnails/${Date.now()}-${thumbnail.name}`;
      console.log('Uploading to path:', thumbnailPath);
      
      const { error: thumbnailError } = await supabase.storage
        .from('thumbnails')
        .upload(thumbnailPath, thumbnail);

      if (thumbnailError) throw thumbnailError;

      thumbnailUrl = supabase.storage
        .from('thumbnails')
        .getPublicUrl(thumbnailPath).data.publicUrl;
      
      console.log('=== THUMBNAIL UPLOAD SUCCESS ===');
      console.log('New thumbnail URL generated:', thumbnailUrl);
      console.log('Previous thumbnail URL:', currentThumbnailUrl);
    } else {
      console.log('=== NO NEW THUMBNAIL ===');
      console.log('Using existing thumbnail URL:', thumbnailUrl);
    }

    // Update template record
    const updateData: any = {
      name,
      template_type: templateType
    };

    // Always include thumbnail_url in the update, whether it changed or not
    updateData.thumbnail_url = thumbnailUrl;

    if (templateType === 'elementor') {
      updateData.json_content = parsedJsonContent;
      updateData.figma_html_data = null; // Clear Figma data if switching to Elementor
    } else if (templateType === 'figma') {
      updateData.figma_html_data = figmaHtmlData;
      updateData.json_content = null; // Clear JSON data if switching to Figma
    }

    console.log('=== DATABASE UPDATE ===');
    console.log('Template ID:', id);
    console.log('Update data being sent to database:', updateData);
    console.log('Thumbnail URL in update data:', updateData.thumbnail_url);

    const { error: updateError } = await supabase
      .from('templates')
      .update(updateData)
      .eq('id', id);

    if (updateError) throw updateError;

    console.log('=== DATABASE UPDATE SUCCESS ===');
    console.log('Template updated successfully in database');
    
    // Verify the update by fetching the record
    const { data: verifyData, error: verifyError } = await supabase
      .from('templates')
      .select('thumbnail_url')
      .eq('id', id)
      .single();
    
    if (verifyError) {
      console.error('Failed to verify update:', verifyError);
    } else {
      console.log('=== VERIFICATION ===');
      console.log('Database now contains thumbnail_url:', verifyData.thumbnail_url);
      console.log('Expected thumbnail_url:', thumbnailUrl);
      console.log('URLs match:', verifyData.thumbnail_url === thumbnailUrl);
    }

    // Tag syncing (unchanged)
    const { data: existingTags, error: existingTagsError } = await supabase
      .from('template_tags')
      .select('tag_id')
      .eq('template_id', id);

    if (existingTagsError) throw existingTagsError;

    const existingTagIds = existingTags.map(tag => tag.tag_id);
    const tagsToRemove = existingTagIds.filter(tagId => !selectedTags.includes(tagId));
    const tagsToAdd = selectedTags.filter(tagId => !existingTagIds.includes(tagId));

    if (tagsToRemove.length > 0) {
      const { error: deleteError } = await supabase
        .from('template_tags')
        .delete()
        .eq('template_id', id)
        .in('tag_id', tagsToRemove);

      if (deleteError) throw deleteError;
    }

    if (tagsToAdd.length > 0) {
      const templateTags = tagsToAdd.map(tagId => ({
        template_id: id,
        tag_id: tagId
      }));

      const { error: tagLinkError } = await supabase
        .from('template_tags')
        .insert(templateTags);

      if (tagLinkError) throw tagLinkError;
    }

    toast.success('Template updated successfully');
    
    // Trigger template grid refresh
    triggerTemplateRefresh();
    
    // Update the current thumbnail URL state if a new one was uploaded
    if (thumbnail) {
      setCurrentThumbnailUrl(thumbnailUrl);
      setThumbnail(null); // Clear the file input
    }
    
    console.log('=== TEMPLATE EDIT SUBMIT END ===');
    navigate('/admin/dashboard');
  } catch (error) {
    console.error('Update error:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to update template');
  } finally {
    setIsSaving(false);
  }
};

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F3F0' }}>
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading template...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#F7F3F0' }}>
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
            <Link
              to="/admin/dashboard"
              className="inline-flex items-center text-red-700 hover:text-red-800"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F3F0' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link
                to="/admin/dashboard"
                className="flex items-center text-gray-700 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-8">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold text-gray-800">Edit Template</h1>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex items-center px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Template
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="templateName" className="block text-sm font-medium text-gray-700 mb-1">
                Template Name
              </label>
              <input
                id="templateName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Template Type
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="templateType"
                    value="elementor"
                    checked={templateType === 'elementor'}
                    onChange={(e) => setTemplateType(e.target.value as 'elementor' | 'figma')}
                    className="mr-2"
                  />
                  <span className="text-sm">Elementor</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="templateType"
                    value="figma"
                    checked={templateType === 'figma'}
                    onChange={(e) => setTemplateType(e.target.value as 'elementor' | 'figma')}
                    className="mr-2"
                  />
                  <span className="text-sm">Figma</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {availableTags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedTags.includes(tag.id)
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    }`}
                  >
                    <Tag className="h-4 w-4 mr-1.5" />
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="thumbnail" className="block text-sm font-medium text-gray-700 mb-1">
                Thumbnail Image
              </label>
              <div className="space-y-2">
                {(thumbnailPreview || currentThumbnailUrl) && (
                  <div className="relative inline-block">
                    <img
                      src={thumbnailPreview || currentThumbnailUrl}
                      alt={thumbnailPreview ? "New thumbnail preview" : "Current thumbnail"}
                      className="h-32 w-auto rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveThumbnail}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <div className="flex items-center">
                  <input
                    id="thumbnail"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setThumbnail(file);
                        const url = URL.createObjectURL(file);
                        setThumbnailPreview(url);
                      } else {
                        setThumbnail(null);
                        setThumbnailPreview(null);
                      }
                    }}
                    className="sr-only"
                  />
                  <label
                    htmlFor="thumbnail"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                  >
                    {thumbnailPreview || currentThumbnailUrl ? 'Change image' : 'Choose image'}
                  </label>
                  {(thumbnailPreview || currentThumbnailUrl) && (
                    <span className="text-sm text-gray-500 ml-3">
                      {thumbnailPreview ? `New: ${thumbnail?.name}` : 'Current image'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {templateType === 'elementor' && (
              <div>
                <label htmlFor="jsonContent" className="block text-sm font-medium text-gray-700 mb-1">
                  Elementor JSON Content
                </label>
                <textarea
                  id="jsonContent"
                  value={jsonContent}
                  onChange={(e) => setJsonContent(e.target.value)}
                  className="w-full h-64 rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Paste your Elementor JSON content here..."
                />
              </div>
            )}

            {templateType === 'figma' && (
              <FigmaPasteArea
                onHtmlDataCapture={setFigmaHtmlData}
                htmlData={figmaHtmlData}
              />
            )}

            <button
              type="submit"
              disabled={isSaving}
              className="w-full bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Saving changes...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Save className="h-5 w-5 mr-2" />
                  Save Changes
                </span>
              )}
            </button>
          </form>
        </div>
      </div>
      
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Template"
        message="Are you sure you want to delete this template? This action cannot be undone."
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
}